import type { ScrollViewInstance } from 'react-native';
import React, { useEffect, useRef } from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { FontAwesome6 } from '@expo/vector-icons';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { interRegular, interSemiBold } from '../../lib/fonts';
import { FeedPaper } from '../../lib/hooks/useProjectFeed';
import { DownvoteReason, FeedbackVote } from '../../lib/projectPaperFeedback';
import ProjectFeedDetailContent from './ProjectFeedDetailContent';
import { Trans } from '@lingui/react/macro';

interface ProjectFeedDetailPaneProps {
    item: FeedPaper | null;
    paperLinkHref?: string;
    feedbackPending: boolean;
    onSetFeedback: (vote: FeedbackVote, reason?: DownvoteReason) => Promise<void>;
    onClearFeedback: () => Promise<void>;
}

export default function ProjectFeedDetailPane({
    item,
    paperLinkHref,
    feedbackPending,
    onSetFeedback,
    onClearFeedback,
}: ProjectFeedDetailPaneProps) {
    const { fontsLoaded } = useFontLoading();
    const scrollRef = useRef<ScrollViewInstance>(null);

    useEffect(() => {
        if (!item) {
            return;
        }

        scrollRef.current?.scrollTo({ y: 0, animated: false });
    }, [item?.id]);

    return (
        <View style={styles.shell} testID="project-feed-detail-pane">
            {item && paperLinkHref ? (
                <ScrollView
                    ref={scrollRef}
                    style={styles.scrollView}
                    contentContainerStyle={styles.scrollContent}
                    showsVerticalScrollIndicator={false}
                    testID="project-feed-detail-scroll"
                >
                    <ProjectFeedDetailContent
                        item={item}
                        paperLinkHref={paperLinkHref}
                        feedbackPending={feedbackPending}
                        onSetFeedback={onSetFeedback}
                        onClearFeedback={onClearFeedback}
                        feedbackButtonAlignment="left"
                    />
                </ScrollView>
            ) : (
                <View style={styles.emptyState} testID="project-feed-empty-detail">
                    <View style={styles.emptyIcon}>
                        <FontAwesome6 name="book-open" size={18} color={colors.text500} />
                    </View>
                    <Text style={[styles.emptyTitle, fontsLoaded && interSemiBold]}><Trans id="feed.projectfeeddetailpane.selectAPaperToExplore" comment="UI copy in components/feed/ProjectFeedDetailPane.tsx">
                        Select a paper to explore
                    </Trans></Text>
                    <Text style={[styles.emptyBody, fontsLoaded && interRegular]}><Trans id="feed.projectfeeddetailpane.readTheAbstractGetThePdfCreateASummaryAnd" comment="UI copy in components/feed/ProjectFeedDetailPane.tsx">
                        Read the abstract, get the PDF, create a summary, and provide feedback to improve relevance filtering.
                    </Trans></Text>
                </View>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    shell: {
        flex: 1,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: 18,
        backgroundColor: colors.card,
        overflow: 'hidden',
        minWidth: 0,
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        paddingBottom: 24,
    },
    emptyState: {
        flex: 1,
        minHeight: 0,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 36,
        gap: 14,
    },
    emptyIcon: {
        width: 46,
        height: 46,
        borderRadius: 23,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f3f5f9',
        borderWidth: 1,
        borderColor: '#dde2ea',
    },
    emptyTitle: {
        fontSize: 24,
        lineHeight: 30,
        color: colors.text900,
        textAlign: 'center',
    },
    emptyBody: {
        maxWidth: 420,
        fontSize: 15,
        lineHeight: 24,
        color: colors.text600,
        textAlign: 'center',
    },
});
